// The Usages sheet: what a usage is, as a table row.
//
// The machinery — columns discovered from the data, rows and columns matched by
// NAME rather than position, a remembered baseline so a change made in Giraffe
// is not mistaken for one the user typed — lives in entityTable.ts, shared with
// Features and Blocks. This file is only what makes a usage a usage.
//
// It holds ORDINARY VALUES. Not formulas, not a bound rectangle the app owns —
// you type over a rate exactly as you would anywhere else, and nothing about
// the cell is special. Push then compares the sheet against the project's live
// usages and sends only the differences.

import type { ColumnList, Sheet, Workbook } from '@/model/types';
import { naturalCompare } from '@/model/collate';
import { USAGES_SHEET_NAME, createSheet, newId, uniqueSheetName, usagesSheet } from '@/model/workbook';
import type { GiraffeData } from '@/data/types';
import { unitsOf } from '@/data/units';
import {
  columnsOf,
  fieldsInColumns as fieldsInColumnsOf,
  tableDiff,
  writeTable,
  type EntityDiff,
  type EntityRow,
  type TableSpec,
} from './entityTable';

export { FIRST_DATA_ROW, HEADER_ROW } from './entityTable';
export type {
  EntityAddition as UsageAddition,
  EntityChange as UsageChange,
  EntityDiff as UsageDiff,
} from './entityTable';

/**
 * Every column A-Z, full stop — a nested one (a dwelling mix, `marker`,
 * `strokeDash`, …) sorts in its own alphabetical place among the rest, not
 * shuffled to the end. `isNested` still does its OTHER job here — Usages
 * shows a nested value as a readable one-liner and never pushes an edit to
 * one back (`entityTable.ts`'s `tableDiff`/`isWritable`) — this only changed
 * where in the column order it lands.
 */
function usageColumnSort(fields: string[]): string[] {
  return [...fields].sort((a, b) => naturalCompare(a, b));
}

function spec(data: GiraffeData): TableSpec {
  const isNested = (field: string): boolean => data.classOf('usages', field) === 'nested';
  return {
    label: 'Name',
    /** Shown as the row label, so not repeated as a column. */
    labelFields: new Set(['name', 'usage']),
    order: [],
    isNested,
    columnSort: usageColumnSort,
    // The provider says which units it handed out; the headers say it too.
    units: unitsOf(data),
    newFields: true,
    // The usage's own property names, verbatim — not prettyField's humanised spelling.
    rawHeaders: true,
  };
}

function rowsOf(data: GiraffeData): EntityRow[] {
  return data
    .rows('usages')
    .map((row) => ({
      name: String(row.properties.name ?? row.id),
      fields: row.properties,
    }))
    .sort((a, b) => naturalCompare(a.name, b.name));
}

/**
 * The usages, as a list another sheet can pick from.
 *
 * Lives here because what counts as a usage — and what it is called — is this
 * file's business, and the Features sheet should not have to know the shape of
 * a usage row to offer them.
 *
 * No colours: the chips take the cycled palette. A usage carries its own map
 * colour, which would be the better chip by far, but chip text is white and a
 * usage colour is typically pale enough that white on it cannot be read — that
 * is a change to how a chip draws, not to what is on this list.
 */
export function usageOptions(data: GiraffeData): ColumnList | undefined {
  const options: string[] = [];
  const seen = new Set<string>();
  for (const row of data.rows('usages')) {
    const name = String(row.properties.name ?? row.id).trim();
    if (name === '' || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    options.push(name);
  }
  return options.length > 0 ? { options } : undefined;
}

export function usageColumns(data: GiraffeData): string[] {
  return columnsOf(rowsOf(data), spec(data));
}

/**
 * Usage rows by name, lowercased — for a caller that needs the FIELDS a
 * matching usage carries (Features' baked-column inheritance), not just the
 * list of names `usageOptions` offers.
 *
 * Keyed the same way a feature's own `usage` property is matched elsewhere in
 * this file's spelling-mismatch comment: lowercased and trimmed, so "Retail"
 * and "retail " find the same row.
 */
export function usageRowsByName(data: GiraffeData): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>();
  for (const row of data.rows('usages')) {
    const name = String(row.properties.name ?? row.id).trim().toLowerCase();
    if (name !== '' && !out.has(name)) out.set(name, row.properties);
  }
  return out;
}

export function ensureUsagesSheet(wb: Workbook): Sheet {
  let sheet = usagesSheet(wb);
  if (!sheet) {
    // A user sheet already called "Usages" keeps its name; the system one takes
    // the next free variant rather than colliding with it.
    sheet = createSheet(uniqueSheetName(wb, USAGES_SHEET_NAME), newId('usages'));
    sheet.role = 'usages';
    sheet.colWidths[1] = 150;
    wb.sheets.push(sheet);
  }
  return sheet;
}

export function writeUsages(sheet: Sheet, data: GiraffeData, preserveEdits = true, wb?: Workbook): void {
  writeTable(sheet, rowsOf(data), spec(data), preserveEdits, wb);
}

export function fullDiff(sheet: Sheet, data: GiraffeData): EntityDiff {
  return tableDiff(sheet, rowsOf(data), spec(data));
}

export function fieldsInColumns(sheet: Sheet, data: GiraffeData, from: number, to: number): string[] {
  return fieldsInColumnsOf(sheet, usageColumns(data), from, to);
}

/**
 * Whether a usage diff may go to the project on its own.
 *
 * Pure, because it is the whole rule and a rule worth being able to read: an
 * edit pushes itself however many cells it touched, and a DELETE does not.
 *
 * A deleted row is `removed`, and pushing while one is pending is worse than
 * useless — the push is followed by a refetch, the project still has the usage
 * because the deletion was not confirmed, and the row the user just deleted
 * comes straight back. A deleted COLUMN never appears here at all: a field with
 * no column is simply absent, and dropping it from every usage goes through
 * `pushFieldRemoval`, which its caller confirms because it cannot be undone.
 *
 * A RENAME holds back for the same reason, though it needs no confirming: it
 * drops the old usage, and doing that on a keystroke would mean a half-typed
 * name deleted the real one. It goes on the ordinary Push button instead.
 */
export function autoPushable(diff: EntityDiff): boolean {
  if (diff.removed.length > 0 || diff.renamed.length > 0) return false;
  return diff.changes.length > 0 || diff.added.length > 0;
}
